import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Lock, Unlock, Zap, Coins, ArrowRightLeft, CheckCircle2, AlertTriangle, Fingerprint } from 'lucide-react';
import { MathService } from '../services/mathService';

export const AbsoluteTreasuryGuard: React.FC = () => {
  const [isEngaging, setIsEngaging] = useState(false);
  const [currentAsset, setCurrentAsset] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [completedAssets, setCompletedAssets] = useState<string[]>([]);

  const stats = MathService.engageAbsoluteReclamation();

  const handleEngage = async () => {
    setIsEngaging(true);
    setResult(null);
    setCompletedAssets([]);
    
    // Folding 1.0 Residue process
    for (const asset of stats.assets) {
        setCurrentAsset(asset);
        await new Promise(r => setTimeout(r, 800));
        setCompletedAssets(prev => [...prev, asset]);
    }
    
    setCurrentAsset(null);
    setResult(stats);
    setIsEngaging(false);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Absolute Treasury <span className="text-lattice-cyan italic">Guard</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Omega Flow: Folding 1.0 Residue into the Sovereign Sink. The final hand-off to the Core.
            </p>
          </div>

          <div className="space-y-8">
            <div className="p-8 rounded-3xl bg-white/5 border border-white/10 relative overflow-hidden group/box">
                <div className="flex items-center justify-between mb-8">
                   <div className="flex items-center gap-3">
                      <Fingerprint className={`w-5 h-5 ${result ? 'text-lattice-cyan' : 'text-white/20'}`} />
                      <span className="text-xs font-mono font-bold uppercase tracking-widest">Vault Signature</span>
                   </div>
                   <div className="text-[10px] font-mono text-white/20 uppercase tracking-widest bg-white/5 px-4 py-1 rounded-full">
                      {stats.status}
                   </div>
                </div>

                <div className="space-y-4 mb-10">
                   {stats.assets.map((asset) => {
                     const isDone = completedAssets.includes(asset);
                     const isActive = currentAsset === asset;
                     return (
                       <div key={asset} className={`p-4 rounded-xl border transition-all duration-500 flex items-center justify-between ${isActive ? 'border-lattice-cyan bg-lattice-cyan/5' : isDone ? 'border-white/20 bg-white/5' : 'border-white/5 opacity-30'}`}>
                          <div className="flex items-center gap-3">
                             <Coins className={`w-4 h-4 ${isActive || isDone ? 'text-lattice-cyan' : 'text-white/10'}`} />
                             <span className="text-xs font-mono font-bold text-white/80">{asset}</span>
                          </div>
                          {isDone ? <CheckCircle2 className="w-4 h-4 text-lattice-cyan" /> : isActive ? <Zap className="w-4 h-4 text-lattice-cyan animate-pulse" /> : <Lock className="w-4 h-4 text-white/5" />}
                       </div>
                     );
                   })}
                </div>

                <button 
                  onClick={handleEngage}
                  disabled={isEngaging}
                  className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
                >
                  {isEngaging ? <ArrowRightLeft className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" />}
                  {isEngaging ? "ESTABLISHING OMEGA FLOW..." : "ENGAGE RECLAMATION FLOW"}
                </button>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black relative flex flex-col justify-between"
                >
                   <div>
                      <div className="flex items-center gap-3 mb-10">
                        <Unlock className="w-6 h-6" />
                        <span className="text-xs font-mono font-bold uppercase tracking-[0.2em]">Flow Established</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Vault Status</span>
                            <p className="text-2xl font-display font-bold tracking-tight uppercase italic">{result.vaultStatus}</p>
                         </div>
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Protection Level</span>
                            <p className="text-xl font-display font-bold tracking-widest uppercase">{result.protectionLevel}</p>
                         </div>
                         <div className="bg-black/10 p-6 rounded-2xl border border-black/10">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Target Address (Sink)</span>
                            <p className="text-[10px] font-mono break-all leading-tight font-bold opacity-80">
                               {result.targetAddress}
                            </p>
                         </div>
                      </div>

                      <div className="mt-10 p-6 border-2 border-black/10 rounded-2xl">
                         <p className="text-xs font-mono font-bold leading-relaxed italic opacity-80">
                           "{result.message}"
                         </p>
                      </div>
                   </div>

                   <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-mono uppercase font-bold opacity-60">Signature Root</span>
                        <span className="text-[10px] font-mono font-bold tracking-tight">{result.signature}</span>
                      </div>
                      <Shield className="w-10 h-10 opacity-20" />
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <Lock className="w-20 h-20 text-white/5" />
                      {isEngaging && (
                         <motion.div 
                           animate={{ scale: [1, 2], opacity: [0.3, 0] }}
                           transition={{ duration: 1.5, repeat: Infinity }}
                           className="absolute inset-0 bg-lattice-cyan rounded-full filter blur-xl"
                         />
                      )}
                   </div>
                   <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed">
                     {isEngaging ? "Aligning 11 Billion Nodes..." : "Awaiting Zero-Drift Synchronization"}
                   </p>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>

      <div className="mt-12 pt-10 border-t border-white/5 flex flex-wrap items-center justify-center gap-12">
         <div className="flex items-center gap-2">
            <Shield className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Absolute Substrate</span>
         </div>
         <div className="flex items-center gap-2">
            <Zap className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">11B Node Protection</span>
         </div>
         <div className="flex items-center gap-2">
            <Fingerprint className="w-3 h-3 text-white/20" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Omega Flow Pattern</span>
         </div>
      </div>
    </div>
  );
};
